import React, { useState, useEffect } from "react";
import BackButton from "../../components/common/backButton";
import contentHooks from "../../hooks/contentHooks";
import { useParams } from "react-router-dom";

const StoryQuestionDetail = () => {
  const { id } = useParams();
  const [contentData, setContentData] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await contentHooks.contentDetail({ id });
        if (result.IsError) {
          setError(true);
        } else {
          setContentData(result);
        }
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const enhanceHtmlContent = (htmlString) => {
    const minimalStyle = `
      <style>
        img {
          max-width: 100% !important;
          height: auto !important;
          display: block;
          margin: 0 auto;
        }
      </style>
    `;
    
    if (htmlString.includes('<body>')) {
      return htmlString.replace('<body>', `<body>${minimalStyle}`);
    } else if (htmlString.includes('<head>')) {
      return htmlString.replace('<head>', `<head>${minimalStyle}`);
    } else {
      return `${minimalStyle}${htmlString}`;
    }
  };

  // HTML 컨텐츠를 가져오는 useEffect
  useEffect(() => {
    const fetchHtmlContent = async () => {
      if (contentData?.data?.content_description && 
          contentData.data.content_description.startsWith('http')) {
        try {
          setIsLoadingHtml(true);
          const response = await fetch(contentData.data.content_description);
          if (response.ok) {
            const htmlText = await response.text();
            // HTML 컨텐츠에 반응형 스타일 적용
            const enhancedHtml = enhanceHtmlContent(htmlText);
            setHtmlContent(enhancedHtml);
          }
        } catch (err) {
          console.error('HTML 컨텐츠를 불러오는데 실패했습니다:', err);
        } finally {
          setIsLoadingHtml(false);
        }
      } else if (contentData?.data?.content_description) {
        // URL이 아닌 경우 그대로 설정하되 스타일 적용
        const enhancedHtml = enhanceHtmlContent(contentData.data.content_description);
        setHtmlContent(enhancedHtml);
      }
    };

    if (contentData && !isLoading) {
      fetchHtmlContent();
    }
  }, [contentData, isLoading]);

  if (isLoading) {
    return (
      <div
        className="flex flex-col pt-[50px] md:pt-[70px] bg-white"
        style={{ 
          minHeight: "calc(var(--vh, 1vh) * 100)",
          paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
        }}
      >
        <BackButton selectedMenu={1} />
        <div className="pb-[30px]">
          <div className="text-center w-full">
            <p>로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || contentData?.IsError) {
    return (
      <div
        className="flex flex-col pt-[50px] md:pt-[70px] bg-white"
        style={{ 
          minHeight: "calc(var(--vh, 1vh) * 100)",
          paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
        }}
      >
        <BackButton />
        <div className="pb-[30px]">
          <div className="text-center w-full">
            <p>데이터를 불러오는데 실패했습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col pt-[50px] bg-white"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <BackButton />
      <div className="pb-[30px]">
        <div>
          {contentData?.data?.content_background_image && (
            <div className="w-full">
              <img
                src={contentData.data.content_background_image}
                alt={contentData.data.content_title}
                className="w-full max-h-[800px]"
              />
            </div>
          )}
          <div className="w-[calc(100%-46px)] md:w-[calc(100%-92px)] mx-auto">
            <div className="text-[20px] md:text-3xl font-semibold my-[40px] text-center">
              {contentData?.data?.content_title}
            </div>
            
            <div className="text-[16px] md:text-xl leading-[27px]">
              {isLoadingHtml ? (
                <div className="text-center py-4">
                  <p>컨텐츠를 불러오는 중...</p>
                </div>
              ) : htmlContent ? (
                <iframe
                  srcDoc={htmlContent}
                  title={`${contentData?.data?.content_title || 'HTML 컨텐츠'} - 상세 내용`}
                  className="w-full min-h-[500px] border-0 rounded-lg"
                  style={{
                    backgroundColor: 'transparent'
                  }}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  loading="lazy"
                  onLoad={(e) => {
                    // iframe 높이를 컨텐츠에 맞게 자동 조정
                    const iframe = e.target;
                    try {
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                      const height = Math.max(
                        iframeDoc.documentElement.scrollHeight,
                        iframeDoc.body.scrollHeight,
                        500 
                      );
                      iframe.style.height = height + 'px';
                      
                      const bodyElement = iframeDoc.body;
                      if (bodyElement) {
                        bodyElement.style.overflow = 'hidden';
                      }
                    } catch (err) {
                      console.log('iframe 높이 조정 실패:', err);
                      // 에러 발생 시 기본 높이 설정
                      iframe.style.height = '600px';
                    }
                  }}
                />
              ) : (
                <div className="text-center py-4">
                  <p>컨텐츠를 불러올 수 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryQuestionDetail;
